import { TRUST_BOUNDARY } from "./constants";
import { formatMoney, sameMoney } from "./money";
import type {
  BuyerFundingReview,
  FieldCrossReference,
  FundingMatch,
  MatchStatus,
  OpenGap,
  SourceRow
} from "./types";

const now = () => new Date().toISOString();

type FlowEvidence = {
  knownEntryPoint: string;
  officePublishedAt: string;
  buyerViewedAt?: string;
  acknowledgmentStatement?: string;
  buyerAcknowledgedAt?: string;
  lookupFileCode?: string;
  lookupBuyerLastName?: string;
  lookupAmountDue?: string;
  conflictReport?: {
    source: string;
    attackerRoutingLast4: string;
    attackerAccountLast4: string;
    reportedAt: string;
    note: string;
  };
  docuSignEnvelopeId?: string;
  docuSignSignedAt?: string;
  docuSignInstructionHash?: string;
  docuSignDocumentHash?: string;
  docuSignSignerEmail?: string;
};

export function sourceRowsFor(review: BuyerFundingReview): SourceRow[] {
  const actor = review.createdBy || "system";
  const recordedAt = review.updatedAt || now();
  const flow = flowEvidenceFor(review);
  return [
    {
      id: `${review.id}-src-expected`,
      reviewId: review.id,
      sourceType: "expected_funds",
      title: review.expectedFunds.sourceDocumentId || "Expected funds source",
      recordedBy: actor,
      recordedAt,
      sourceHash: review.fundingInstruction.hash,
      extractedFields: {
        amount: review.expectedFunds.amount?.amountCents ?? null,
        purpose: review.expectedFunds.purpose,
        dueAt: review.expectedFunds.dueAt ?? null,
        sourceStatus: review.expectedFunds.sourceStatus
      },
      confidence: review.expectedFunds.extractionConfidence,
      limitation: "Expected funds are recorded from the file source or manual operator entry.",
      claimIds: ["expected_amount", "funding_purpose", "due_date"]
    },
    {
      id: `${review.id}-src-instruction`,
      reviewId: review.id,
      sourceType: "funding_instruction",
      title: review.fundingInstruction.sourceDocumentId || "Funding instruction",
      recordedBy: actor,
      recordedAt,
      sourceHash: review.fundingInstruction.hash,
      extractedFields: {
        version: review.fundingInstruction.versionLabel ?? null,
        sentAt: review.fundingInstruction.sentAt ?? null,
        rail: review.fundingInstruction.allowedRails.join(", "),
        accountLast4: review.fundingInstruction.receivingAccountLast4 ?? null,
        routingLast4: review.fundingInstruction.routingLast4 ?? null,
        beneficiary: review.fundingInstruction.beneficiaryName ?? null,
        bank: review.fundingInstruction.receivingBankName ?? null,
        officePublishedAt: flow?.officePublishedAt ?? null
      },
      limitation: "Funding instructions are recorded as the instruction the office sent or relied on.",
      claimIds: ["instruction_version", "receiving_account", "routing_last_four", "beneficiary", "bank", "allowed_rail", "office_published_timestamp"]
    },
    ...flowSourceRowsFor(review, actor, recordedAt, flow),
    {
      id: `${review.id}-src-ack`,
      reviewId: review.id,
      sourceType: "buyer_acknowledgment",
      title: review.buyerAcknowledgment.sourceDocumentId || review.buyerAcknowledgment.type,
      recordedBy: review.buyerAcknowledgment.recordedBy || actor,
      recordedAt: review.buyerAcknowledgment.acknowledgedAt || recordedAt,
      extractedFields: {
        party: review.buyerAcknowledgment.partyName ?? null,
        amount: review.buyerAcknowledgment.acknowledgedAmount?.amountCents ?? null,
        instructionVersion: review.buyerAcknowledgment.acknowledgedInstructionVersion ?? null,
        statement: flow?.acknowledgmentStatement ?? null,
        buyerAcknowledgedAt: flow?.buyerAcknowledgedAt ?? review.buyerAcknowledgment.acknowledgedAt ?? null
      },
      limitation: review.buyerAcknowledgment.limitation,
      claimIds: ["buyer_acknowledgment", "acknowledged_instruction", "acknowledged_amount", "buyer_acknowledgment_statement", "buyer_acknowledged_timestamp"]
    },
    {
      id: `${review.id}-src-source`,
      reviewId: review.id,
      sourceType: "source_account_evidence",
      title: review.sourceAccountEvidence.evidenceType,
      provider: review.sourceAccountEvidence.provider,
      recordedBy: actor,
      recordedAt: review.sourceAccountEvidence.recordedAt || recordedAt,
      extractedFields: {
        ownerName: review.sourceAccountEvidence.ownerName ?? null,
        ownerMatchResult: review.sourceAccountEvidence.ownerMatchResult,
        bankName: review.sourceAccountEvidence.bankName ?? null,
        accountLast4: review.sourceAccountEvidence.accountLast4 ?? null
      },
      limitation: review.sourceAccountEvidence.limitation,
      claimIds: ["source_account_evidence", "source_party", "owner_match"]
    },
    {
      id: `${review.id}-src-initiation`,
      reviewId: review.id,
      sourceType: "payment_initiation",
      title: review.paymentInitiation.sourceDocumentId || review.paymentInitiation.evidenceType,
      recordedBy: actor,
      recordedAt: review.paymentInitiation.initiatedAt || recordedAt,
      extractedFields: {
        amount: review.paymentInitiation.initiatedAmount?.amountCents ?? null,
        senderShown: review.paymentInitiation.senderShown ?? null,
        rail: review.paymentInitiation.rail,
        reference: review.paymentInitiation.reference ?? null
      },
      limitation: review.paymentInitiation.limitation,
      claimIds: ["payment_initiation", "initiated_amount", "trace_reference"]
    },
    {
      id: `${review.id}-src-inbound`,
      reviewId: review.id,
      sourceType: "inbound_receipt",
      title: review.inboundReceipt.sourceDocumentId || review.inboundReceipt.sourceType,
      recordedBy: review.inboundReceipt.accountingReviewer || actor,
      recordedAt: review.inboundReceipt.receivedAt || recordedAt,
      extractedFields: {
        amount: review.inboundReceipt.amountReceived?.amountCents ?? null,
        senderShown: review.inboundReceipt.senderShown ?? null,
        rail: review.inboundReceipt.rail ?? null,
        reference: review.inboundReceipt.reference ?? null,
        ledger: review.inboundReceipt.trustLedgerEntryId ?? null
      },
      limitation: "Inbound receipt records what arrived in the trust account, ledger, payment provider, or manual accounting note.",
      claimIds: ["inbound_receipt", "received_amount", "sender_shown", "ledger_entry"]
    },
    {
      id: `${review.id}-src-office-action`,
      reviewId: review.id,
      sourceType: "office_action",
      title: "Office action record",
      recordedBy: review.officeAction.recordedBy || actor,
      recordedAt: review.officeAction.recordedAt || recordedAt,
      extractedFields: {
        action: review.officeAction.action,
        fundsStatus: review.officeAction.fundsStatus,
        ledgerReference: review.officeAction.ledgerReference || review.inboundReceipt.trustLedgerEntryId || null,
        managerReviewStatus: review.officeAction.managerReviewStatus || null
      },
      limitation: "Office action records the office decision made outside Veto authority.",
      claimIds: ["office_action", "funds_status", "ledger_reference", "manager_review_status"]
    },
    {
      id: `${review.id}-src-signoff`,
      reviewId: review.id,
      sourceType: "signoff",
      title: "Reviewer sign-off",
      recordedBy: review.signoff.signerName || actor,
      recordedAt: review.signoff.signedAt || recordedAt,
      extractedFields: {
        signerName: review.signoff.signerName || null,
        role: review.signoff.role || null,
        fundsStatusAtSignoff: review.signoff.fundsStatusAtSignoff || review.fundsStatus,
        gapsAcknowledged: review.signoff.gapsAcknowledged,
        officeAction: review.signoff.officeAction || review.officeAction.action,
        statementVersion: review.signoff.statementVersion
      },
      limitation: "Sign-off records reviewer completion and retained limitations for the file.",
      claimIds: ["reviewer_signoff", "gaps_acknowledged", "action_at_signoff", "signoff_statement"]
    },
    {
      id: `${review.id}-src-export`,
      reviewId: review.id,
      sourceType: "export",
      title: "Receipt export package",
      recordedBy: review.signoff.signerName || actor,
      recordedAt: review.exportedAt || review.signoff.signedAt || recordedAt,
      extractedFields: {
        receiptId: review.reviewNumber,
        receiptVersion: review.receiptVersion,
        saveBackLocation: review.saveBackLocation || null,
        exportedAt: review.exportedAt || null
      },
      limitation: "Export records the packet created from the review data and source rows at export time.",
      claimIds: [
        "receipt_export",
        "receipt_hash",
        "packet_json",
        "event_log",
        "source_manifest",
        "field_cross_reference",
        "save_back_note"
      ]
    }
  ];
}

export function computeMatches(review: BuyerFundingReview): FundingMatch[] {
  const sourceRows = review.sourceRows.length ? review.sourceRows : sourceRowsFor(review);
  const sourceIds = Object.fromEntries(sourceRows.map((source) => [source.sourceType, source.id]));
  const expectedAmount = review.expectedFunds.amount || review.expectedAmount;
  const amountResult: MatchStatus =
    sameMoney(expectedAmount, review.inboundReceipt.amountReceived) &&
    (!review.paymentInitiation.initiatedAmount ||
      sameMoney(expectedAmount, review.paymentInitiation.initiatedAmount))
      ? "Matched"
      : review.inboundReceipt.amountReceived
        ? "Partially matched"
        : "Not recorded";

  const instructionResult: MatchStatus =
    review.fundingInstruction.versionLabel &&
    review.buyerAcknowledgment.acknowledgedInstructionVersion &&
    normalize(review.fundingInstruction.versionLabel) ===
      normalize(review.buyerAcknowledgment.acknowledgedInstructionVersion)
      ? "Matched"
      : review.buyerAcknowledgment.acknowledgedInstructionVersion
        ? "Did not match"
        : "Not recorded";

  const receivingResult: MatchStatus =
    review.fundingInstruction.receivingAccountLast4 &&
    review.inboundReceipt.receivingAccountLast4 &&
    review.fundingInstruction.receivingAccountLast4 === review.inboundReceipt.receivingAccountLast4
      ? "Matched"
      : review.inboundReceipt.receivingAccountLast4
        ? "Did not match"
        : "Unable to check";

  const railResult: MatchStatus =
    review.inboundReceipt.rail && review.fundingInstruction.allowedRails.includes(review.inboundReceipt.rail)
      ? "Matched"
      : review.inboundReceipt.rail
        ? "Did not match"
        : "Unable to check";

  const sourceName = review.sourceAccountEvidence.sourceParty || review.fundingSource.fundingPartyName;
  const senderResult: MatchStatus =
    sourceName && review.inboundReceipt.senderShown && normalize(sourceName) === normalize(review.inboundReceipt.senderShown)
      ? "Matched"
      : review.inboundReceipt.senderShown
        ? "Partially matched"
        : "Unable to check";

  const referenceResult: MatchStatus =
    review.inboundReceipt.reference?.includes(review.fileNumber) ||
    review.inboundReceipt.memo?.includes(review.fileNumber) ||
    review.paymentInitiation.fileMemo?.includes(review.fileNumber)
      ? "Matched"
      : review.inboundReceipt.reference || review.inboundReceipt.memo
        ? "Did not match"
        : "Not recorded";

  const timingResult: MatchStatus =
    review.expectedFunds.dueAt && review.inboundReceipt.receivedAt
      ? new Date(review.inboundReceipt.receivedAt).getTime() <= new Date(review.expectedFunds.dueAt).getTime()
        ? "Matched"
        : "Did not match"
      : "Unable to check";

  const returnRiskResult: MatchStatus =
    !review.inboundReceipt.returnRiskStatus ||
    review.inboundReceipt.returnRiskStatus === "none_recorded"
      ? "Matched"
      : "Open gap retained";

  const rows: Array<Omit<FundingMatch, "id" | "reviewId">> = [
    {
      rowType: "buyer_file",
      label: "Buyer / file",
      result:
        review.inboundReceipt.fileNumber && review.inboundReceipt.fileNumber === review.fileNumber
          ? "Matched"
          : review.inboundReceipt.fileNumber
            ? "Did not match"
            : "Unable to check",
      expectedValue: review.fileNumber,
      observedValue: review.inboundReceipt.fileNumber,
      sourceRowIds: [sourceIds.expected_funds, sourceIds.inbound_receipt].filter(Boolean),
      note: "Does the buyer/file match across documents?"
    },
    {
      rowType: "amount",
      label: "Amount",
      result: amountResult,
      expectedValue: formatMoney(expectedAmount),
      observedValue: formatMoney(review.inboundReceipt.amountReceived),
      sourceRowIds: [sourceIds.expected_funds, sourceIds.payment_initiation, sourceIds.inbound_receipt].filter(Boolean),
      note: "Does expected amount match initiated and received amount?"
    },
    {
      rowType: "purpose",
      label: "Purpose",
      result: review.expectedFunds.purpose === review.fundingPurpose ? "Matched" : "Partially matched",
      expectedValue: review.fundingPurpose,
      observedValue: review.expectedFunds.purpose,
      sourceRowIds: [sourceIds.expected_funds].filter(Boolean),
      note: "Does payment purpose match cash to close, EMD, shortage, or other recorded purpose?"
    },
    {
      rowType: "instruction_version",
      label: "Instruction version",
      result: instructionResult,
      expectedValue: review.fundingInstruction.versionLabel,
      observedValue: review.buyerAcknowledgment.acknowledgedInstructionVersion,
      sourceRowIds: [sourceIds.funding_instruction, sourceIds.buyer_acknowledgment].filter(Boolean),
      note: "Did buyer use the active instruction version?"
    },
    {
      rowType: "receiving_account",
      label: "Receiving account",
      result: receivingResult,
      expectedValue: review.fundingInstruction.receivingAccountLast4,
      observedValue: review.inboundReceipt.receivingAccountLast4,
      sourceRowIds: [sourceIds.funding_instruction, sourceIds.inbound_receipt].filter(Boolean),
      note: "Did funds land where expected?"
    },
    {
      rowType: "payment_rail",
      label: "Payment rail",
      result: railResult,
      expectedValue: review.fundingInstruction.allowedRails.join(", "),
      observedValue: review.inboundReceipt.rail,
      sourceRowIds: [sourceIds.funding_instruction, sourceIds.payment_initiation, sourceIds.inbound_receipt].filter(Boolean),
      note: "Was the rail allowed?"
    },
    {
      rowType: "sender_source",
      label: "Sender/source",
      result: senderResult,
      expectedValue: sourceName,
      observedValue: review.inboundReceipt.senderShown,
      sourceRowIds: [sourceIds.source_account_evidence, sourceIds.inbound_receipt].filter(Boolean),
      note: "Does sender/source match buyer or office-recorded funding source?"
    },
    {
      rowType: "reference",
      label: "Reference",
      result: referenceResult,
      expectedValue: review.fileNumber,
      observedValue: review.inboundReceipt.reference || review.inboundReceipt.memo,
      sourceRowIds: [sourceIds.payment_initiation, sourceIds.inbound_receipt].filter(Boolean),
      note: "Does memo / file number / trace match?"
    },
    {
      rowType: "timing",
      label: "Timing",
      result: timingResult,
      expectedValue: review.expectedFunds.dueAt,
      observedValue: review.inboundReceipt.receivedAt,
      sourceRowIds: [sourceIds.expected_funds, sourceIds.inbound_receipt].filter(Boolean),
      note: "Did funds arrive before cutoff?"
    },
    {
      rowType: "return_risk",
      label: "Return risk",
      result: returnRiskResult,
      expectedValue: "No open return or reversal risk recorded",
      observedValue: review.inboundReceipt.returnRiskStatus,
      sourceRowIds: [sourceIds.inbound_receipt].filter(Boolean),
      note: "Is return/reversal risk recorded?"
    },
    {
      rowType: "exception",
      label: "Exceptions",
      result: review.openGaps.length ? "Open gap retained" : "Not applicable",
      expectedValue: "Gaps acknowledged and office action recorded",
      observedValue: review.officeAction.action,
      sourceRowIds: [...review.openGaps.flatMap((gap) => gap.sourceRowIds), sourceIds.office_action].filter(Boolean),
      note: "Are gaps acknowledged and office action recorded?"
    }
  ];

  return rows.map((row) => ({
    id: `${review.id}-match-${row.rowType}`,
    reviewId: review.id,
    ...row
  }));
}

export function inferOpenGaps(review: BuyerFundingReview): OpenGap[] {
  const gaps = [...review.openGaps];
  const add = (gap: Omit<OpenGap, "id" | "reviewId">) => {
    if (gaps.some((existing) => existing.gapType === gap.gapType && existing.description === gap.description)) return;
    gaps.push({ ...gap, id: `${review.id}-gap-${gaps.length + 1}`, reviewId: review.id });
  };

  const matches = computeMatches(review);
  for (const match of matches) {
    if (match.result === "Matched" || match.result === "Not applicable") continue;
    if (match.rowType === "amount") {
      add({
        gapType: "Amount mismatch",
        severity: match.result === "Did not match" ? "exception_required" : "requires_attention",
        description: "Expected, initiated, and received amounts did not fully match.",
        sourceRowIds: match.sourceRowIds,
        officeActionRequired: true,
        exceptionRequired: true,
        status: "retained"
      });
    }
    if (match.rowType === "sender_source") {
      add({
        gapType: "Sender/source mismatch",
        severity: "exception_required",
        description: "Inbound sender/source did not fully match buyer or office-recorded funding source.",
        sourceRowIds: match.sourceRowIds,
        officeActionRequired: true,
        exceptionRequired: true,
        status: "retained"
      });
    }
    if (match.rowType === "instruction_version") {
      add({
        gapType: "Instruction mismatch",
        severity: "stop_hold",
        description: "Acknowledgment or payment evidence does not show active instruction version.",
        sourceRowIds: match.sourceRowIds,
        officeActionRequired: true,
        exceptionRequired: true,
        status: "retained"
      });
    }
    if (match.rowType === "reference") {
      add({
        gapType: "Missing file number / memo",
        severity: "requires_attention",
        description: "File number or memo reference did not match or was not recorded.",
        sourceRowIds: match.sourceRowIds,
        officeActionRequired: true,
        status: "retained"
      });
    }
    if (match.rowType === "return_risk") {
      add({
        gapType: "ACH return risk",
        severity: "requires_attention",
        description: "Return or reversal risk remains open on the receipt.",
        sourceRowIds: match.sourceRowIds,
        officeActionRequired: true,
        status: "retained"
      });
    }
  }

  if (review.buyerAcknowledgment.type === "Not obtained") {
    add({
      gapType: "Missing buyer acknowledgment",
      severity: "requires_attention",
      description: "Buyer acknowledgment was not recorded.",
      sourceRowIds: [`${review.id}-src-ack`],
      officeActionRequired: true,
      status: "retained"
    });
  }

  if (review.sourceAccountEvidence.evidenceType === "Unable to check") {
    add({
      gapType: "Missing source account evidence",
      severity: "requires_attention",
      description: "Source account evidence was unavailable.",
      sourceRowIds: [`${review.id}-src-source`],
      officeActionRequired: true,
      status: "retained"
    });
  }

  return gaps;
}

export function statusLine(review: BuyerFundingReview): string {
  if (review.status === "Hold / conflicting instructions reported") {
    return `Hold / conflicting instructions reported · Office action: ${review.officeAction.action} · Funds: ${review.fundsStatus}`;
  }
  const matches = review.matches.length ? review.matches : computeMatches(review);
  const gaps = review.openGaps.length ? review.openGaps : inferOpenGaps(review);
  const hasAmountShortage = gaps.some((gap) => gap.gapType === "Amount mismatch" && gap.description.toLowerCase().includes("short"));
  const hasSourceMismatch = gaps.some((gap) => gap.gapType === "Sender/source mismatch");
  const hasReturnRisk = gaps.some((gap) => gap.gapType === "ACH return risk");
  const allCoreMatched = matches
    .filter((match) => match.rowType !== "exception")
    .every((match) => match.result === "Matched" || match.result === "Not applicable");
  const matchState = allCoreMatched
    ? "inbound funds matched"
    : hasAmountShortage
      ? "shortage retained"
      : hasReturnRisk
        ? "open return risk retained"
        : hasSourceMismatch
          ? "source mismatch retained"
          : gaps.length
            ? "open gaps retained"
            : "expected but not received";
  return `Recorded - ${matchState} · Office action: ${review.officeAction.action} · Funds: ${review.fundsStatus}`;
}

export function fieldCrossReference(review: BuyerFundingReview): FieldCrossReference[] {
  const matches = review.matches.length ? review.matches : computeMatches(review);
  const byType = Object.fromEntries(matches.map((match) => [match.rowType, match]));
  const flow = flowEvidenceFor(review);
  return [
    {
      field: "Expected amount",
      source: review.expectedFunds.sourceDocumentId || "Expected funds source",
      evidence: "Buyer acknowledgment + inbound receipt",
      result: byType.amount?.result || "Not recorded"
    },
    {
      field: "Instruction version",
      source: review.fundingInstruction.sourceDocumentId || "Funding instruction",
      evidence: review.buyerAcknowledgment.sourceDocumentId || review.buyerAcknowledgment.type,
      result: byType.instruction_version?.result || "Not recorded"
    },
    {
      field: "Instruction bank",
      source: review.fundingInstruction.sourceDocumentId || "Funding instruction",
      evidence: review.fundingInstruction.receivingBankName || "Not recorded",
      result: review.fundingInstruction.receivingBankName ? "Recorded" : "Not recorded"
    },
    {
      field: "Instruction routing/account last four",
      source: review.fundingInstruction.sourceDocumentId || "Funding instruction",
      evidence: `routing ending ${review.fundingInstruction.routingLast4 || "not recorded"} / account ending ${review.fundingInstruction.receivingAccountLast4 || "not recorded"}`,
      result: review.fundingInstruction.routingLast4 && review.fundingInstruction.receivingAccountLast4 ? "Recorded" : "Not recorded"
    },
    {
      field: "Office published timestamp",
      source: "Instruction publication",
      evidence: flow?.officePublishedAt || review.fundingInstruction.sentAt || "Not recorded",
      result: flow?.officePublishedAt || review.fundingInstruction.sentAt ? "Recorded" : "Not recorded"
    },
    {
      field: "Known Veto entry point",
      source: "Known-entry buyer view",
      evidence: flow?.knownEntryPoint || "Not recorded",
      result: flow?.knownEntryPoint ? "Recorded" : "Not recorded"
    },
    {
      field: "Buyer viewed timestamp",
      source: "Known-entry buyer view",
      evidence: flow?.buyerViewedAt || "Not recorded",
      result: flow?.buyerViewedAt ? "Recorded" : "Not recorded"
    },
    {
      field: "Buyer acknowledgment statement",
      source: review.buyerAcknowledgment.sourceDocumentId || "Buyer acknowledgment",
      evidence: flow?.acknowledgmentStatement || "Not recorded",
      result: flow?.acknowledgmentStatement ? "Recorded" : "Not recorded"
    },
    {
      field: "Source account owner",
      source: review.sourceAccountEvidence.evidenceType,
      evidence: review.sourceAccountEvidence.ownerName || "Owner not recorded",
      result: review.sourceAccountEvidence.ownerMatchResult
    },
    {
      field: "Payment amount",
      source: review.paymentInitiation.sourceDocumentId || review.paymentInitiation.evidenceType,
      evidence: review.inboundReceipt.sourceDocumentId || review.inboundReceipt.sourceType,
      result: byType.amount?.result || "Not recorded"
    },
    {
      field: "Sender",
      source: review.inboundReceipt.sourceDocumentId || review.inboundReceipt.sourceType,
      evidence: review.fundingSource.fundingPartyName || "Funding party not recorded",
      result: byType.sender_source?.result || "Not recorded"
    },
    {
      field: "File number",
      source: review.inboundReceipt.reference || review.inboundReceipt.memo || "Inbound reference",
      evidence: review.fileNumber,
      result: byType.reference?.result || "Not recorded"
    },
    {
      field: "Office action",
      source: "Office action record",
      evidence: review.officeAction.action,
      result: "Recorded"
    },
    {
      field: "Reviewer sign-off",
      source: "Reviewer sign-off",
      evidence: review.signoff.signerName || review.createdBy,
      result: review.signoff.signedAt ? "Recorded" : "Not recorded"
    },
    {
      field: "Receipt export",
      source: "Receipt export package",
      evidence: review.reviewNumber,
      result: "Recorded"
    },
    {
      field: "Save-back note",
      source: "Receipt export package",
      evidence: review.saveBackLocation || "Save-back location not recorded",
      result: review.saveBackLocation ? "Recorded" : "Not recorded"
    },
    ...(flow?.conflictReport
      ? [
          {
            field: "Conflicting instructions report",
            source: "conflicting_instruction_report",
            evidence: flow.conflictReport.source,
            result: "Recorded" as const
          },
          {
            field: "Conflict routing/account last four",
            source: "conflicting_instruction_report",
            evidence: `routing ending ${flow.conflictReport.attackerRoutingLast4} / account ending ${flow.conflictReport.attackerAccountLast4}`,
            result: "Recorded" as const
          },
          {
            field: "Conflict office action",
            source: "conflicting_instruction_report",
            evidence: review.officeAction.action,
            result: "Recorded" as const
          }
        ]
      : [])
  ];
}

export function assertReceiptLanguage(text: string): string[] {
  const lowered = text.toLowerCase();
  const banned = ["buyer verified", "good funds confirmed", "funds approved", "buyer approved", "safe to close", "safe to wire", "fraud detected", "fraud prevented", "bank account confirmed", "identity verified", "payment authorized", "funds guaranteed", "funds insured"];
  return banned.filter((phrase) => lowered.includes(phrase));
}

export function boundaryStatement(): string {
  return TRUST_BOUNDARY;
}

function normalize(value?: string): string {
  return (value || "").trim().toLowerCase();
}

function flowEvidenceFor(review: BuyerFundingReview): FlowEvidence | undefined {
  return (review as BuyerFundingReview & { buyerFundingFlow?: FlowEvidence }).buyerFundingFlow;
}

function flowSourceRowsFor(
  review: BuyerFundingReview,
  actor: string,
  recordedAt: string,
  flow?: FlowEvidence
): SourceRow[] {
  if (!flow) return [];
  const rows: SourceRow[] = [
    {
      id: `${review.id}-src-buyer-entry`,
      reviewId: review.id,
      sourceType: "buyer_entry",
      title: "Known-entry buyer view",
      recordedBy: flow.buyerAcknowledgedAt ? review.buyerParties[0]?.name || actor : actor,
      recordedAt: flow.buyerViewedAt || flow.officePublishedAt || recordedAt,
      extractedFields: {
        knownEntryPoint: flow.knownEntryPoint,
        lookupFileCode: flow.lookupFileCode ?? null,
        lookupBuyerLastName: flow.lookupBuyerLastName ?? null,
        lookupAmountDue: flow.lookupAmountDue ?? null,
        buyerViewedAt: flow.buyerViewedAt ?? null,
        acknowledgmentStatement: flow.acknowledgmentStatement ?? null,
        buyerAcknowledgedAt: flow.buyerAcknowledgedAt ?? null
      },
      limitation: "Known-entry buyer evidence records what the buyer viewed or acknowledged in the fake smoke path.",
      claimIds: [
        "known_veto_entry_point",
        "buyer_lookup_file_code",
        "buyer_lookup_last_name",
        "buyer_lookup_amount_due",
        "buyer_viewed_timestamp",
        "buyer_acknowledgment_statement",
        "buyer_acknowledged_timestamp"
      ]
    },
    {
      id: `${review.id}-src-instruction-publication`,
      reviewId: review.id,
      sourceType: "instruction_publication",
      title: "Instruction version publication",
      recordedBy: actor,
      recordedAt: flow.officePublishedAt,
      extractedFields: {
        instructionVersion: review.fundingInstruction.versionLabel ?? null,
        officePublishedAt: flow.officePublishedAt,
        knownEntryPoint: flow.knownEntryPoint
      },
      limitation: "Instruction publication records what the office made visible from the known Veto entry point.",
      claimIds: ["office_published_timestamp", "instruction_version", "known_veto_entry_point"]
    }
  ];

  if (flow.docuSignEnvelopeId) {
    rows.push({
      id: `${review.id}-src-ack-esign`,
      reviewId: review.id,
      sourceType: "buyer_acknowledgment_esign",
      title: "Buyer Funding Acknowledgment — DocuSign",
      provider: "docusign",
      recordedBy: review.buyerAcknowledgment.recordedBy || review.buyerParties[0]?.name || actor,
      recordedAt: flow.docuSignSignedAt || flow.buyerAcknowledgedAt || recordedAt,
      sourceHash: flow.docuSignDocumentHash,
      extractedFields: {
        envelopeId: flow.docuSignEnvelopeId,
        instructionVersion: review.buyerAcknowledgment.acknowledgedInstructionVersion ?? null,
        instructionHash: flow.docuSignInstructionHash ?? null,
        documentHash: flow.docuSignDocumentHash ?? null,
        signerEmail: flow.docuSignSignerEmail ?? null,
        signedAt: flow.docuSignSignedAt ?? null
      },
      limitation: review.buyerAcknowledgment.limitation,
      claimIds: [
        "buyer_acknowledgment_esign",
        "acknowledged_instruction",
        "buyer_acknowledged_timestamp",
        "buyer_acknowledgment_statement"
      ]
    });
  }

  if (flow.conflictReport) {
    rows.push({
      id: `${review.id}-src-conflict-report`,
      reviewId: review.id,
      sourceType: "conflicting_instruction_report",
      title: "conflicting_instruction_report",
      recordedBy: review.buyerParties[0]?.name || actor,
      recordedAt: flow.conflictReport.reportedAt,
      extractedFields: {
        source: flow.conflictReport.source,
        attackerRoutingLast4: flow.conflictReport.attackerRoutingLast4,
        attackerAccountLast4: flow.conflictReport.attackerAccountLast4,
        reportedAt: flow.conflictReport.reportedAt,
        status: review.status,
        officeAction: review.officeAction.action
      },
      limitation: "Conflict report records buyer-entered fake attacker instruction details for smoke coverage only.",
      claimIds: [
        "conflicting_instruction_report",
        "conflict_source",
        "attacker_routing_last_four",
        "attacker_account_last_four",
        "hold_status",
        "office_action"
      ]
    });
  }
  return rows;
}
